import { Suspense } from 'react';
import Modal from './Modal';
import OneLeagueResource from '../resources/League/One';
import { Header } from './Header';
import Action from './Action';
import useCopy from '../util/useCopy';
import {
  deleteLeagueMutation,
  setLeagueStrictModeMutation,
} from '../resources/League/mutations';
import { pluralize } from '@siteless/util';
import Button from './Button';
import { useState } from 'react';
import { ScoringModeInput } from './ScoringModeInput';
import { CurrentUserMustResource } from '../resources/Auth/CurrentUserMust';
import AllUserProfilesResource from '../resources/AllUserProfiles';
import BallotsCompletedResource from '../resources/Ballot/Completed';
import BallotsStartedResource from '../resources/Ballot/Started';
import { getIsProfileFinished } from '../resources/Auth/services';
import ManageMembersModal from './ManageMembersModal';
import EditLeagueNameModal from './EditLeagueNameModal';
import EditIcon from './EditIcon';
import TrashIcon from './TrashIcon';
import ShareIcon from './ShareIcon';
import CopyIcon from './CopyIcon';
import CheckCircleIcon from './CheckCircleIcon';
import copyToClipboard from 'copy-to-clipboard';
import { makeLink } from '../util/makeLink';
import useShare from '../util/useShare';
import QrCodeIcon from './QrCodeIcon';
import QrCodeModal from './QrCodeModal';
import Loader from './Loader';

let LeagueSettingsModalContent = ({
  onClose,
  leagueId,
  showShareButton,
}: {
  onClose: () => void;
  leagueId: string;
  showShareButton?: boolean;
}) => {
  let currentUser = CurrentUserMustResource.use();
  let copy = useCopy();
  let league = OneLeagueResource.use(leagueId);
  let enums = AllUserProfilesResource.use();
  let completed = BallotsCompletedResource.use();
  let started = BallotsStartedResource.use();

  let { share, isSharing, isSupported: isShareSupported } = useShare();
  let [showManageMembersModal, setShowManageMembersModal] = useState(false);
  let [showEditNameModal, setShowEditNameModal] = useState(false);
  let [showQrCodeModal, setShowQrCodeModal] = useState(false);

  if (!league) return null;

  let completedCount = league.memberUserIds.filter(
    (userId) => completed[userId],
  ).length;

  let inProgressCount = league.memberUserIds.filter(
    (userId) => started[userId] && !completed[userId],
  ).length;

  let notStartedCount = league.memberUserIds.filter(
    (userId) => !started[userId] && getIsProfileFinished(enums.users[userId]),
  ).length;

  let settingUpCount = league.memberUserIds.filter(
    (userId) => !getIsProfileFinished(enums.users[userId]),
  ).length;

  return (
    <>
      <ManageMembersModal
        isOpen={showManageMembersModal}
        onClose={() => setShowManageMembersModal(false)}
        leagueId={leagueId}
      />
      <EditLeagueNameModal
        isOpen={showEditNameModal}
        onClose={() => setShowEditNameModal(false)}
        leagueId={leagueId}
        currentName={league.name}
        userId={currentUser.id}
      />
      {league.joinLinkId && (
        <QrCodeModal
          isOpen={showQrCodeModal}
          onClose={() => setShowQrCodeModal(false)}
          url={makeLink(league.joinLinkId)}
          leagueName={league.name}
        />
      )}
      <div className="relative flex h-full flex-col">
        <div className="flex-none bg-white">
          <Header className="mx-4 border-b-4 border-neutral-100 px-2 py-4">
            {copy.Settings}
          </Header>
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
          <div className="border-b-4 border-neutral-100 pb-4">
            <div className="mb-1 flex items-center justify-between">
              <div className="text-lg font-medium text-purple-900">
                {pluralize({
                  count: league.memberUserIds.length,
                  singular: copy.member,
                })}
              </div>
              <Action
                className="text-sm text-purple-900"
                onClick={() => setShowManageMembersModal(true)}
              >
                {copy.Manage}
              </Action>
            </div>
            <div className="text-sm text-neutral-600">
              {completedCount > 0 && (
                <div>
                  {pluralize({
                    count: completedCount,
                    singular: copy.completedBallot,
                  })}
                  .
                </div>
              )}
              {inProgressCount > 0 && (
                <div>
                  {pluralize({
                    count: inProgressCount,
                    singular: copy.ballot,
                  })}{' '}
                  {copy.inProgress}.
                </div>
              )}
              {notStartedCount > 0 && (
                <div>
                  {pluralize({
                    count: notStartedCount,
                    singular: copy.hasntStartedABallot,
                    plural: copy.haventStartedBallots,
                  })}
                  .
                </div>
              )}
              {settingUpCount > 0 && (
                <div>
                  {settingUpCount} {copy.stillSettingUp}{' '}
                  {pluralize({
                    count: settingUpCount,
                    singular: copy.profile,
                    hideCount: true,
                  })}
                  .
                </div>
              )}
            </div>
            <div className="mt-2 text-sm text-neutral-500">
              {copy.LeagueCreated}{' '}
              {new Intl.DateTimeFormat('en-US', {
                dateStyle: 'long',
              }).format(new Date(league.createdAt.seconds * 1000))}
              .
            </div>
          </div>
        </div>
        <div className="flex-none bg-white px-6 py-4">
          <ScoringModeInput
            stacked
            value={Boolean(league.strictMode)}
            onChange={(strictMode) => {
              setLeagueStrictModeMutation({
                leagueId,
                userId: currentUser.id,
                strictMode,
              });
            }}
          />
          <div className="mb-2 text-sm font-medium uppercase text-purple-900">
            {copy.LeagueName}
          </div>
          <div
            className="mb-3 flex cursor-pointer items-center rounded-xl border-4 border-neutral-200 px-3 py-2"
            onClick={() => setShowEditNameModal(true)}
          >
            <div className="min-w-0 flex-1 truncate text-purple-900">
              {league.name}
            </div>
            <EditIcon className="ml-2 h-5 w-5 flex-none text-rose-500" />
          </div>
          <div className="flex">
            {showShareButton &&
              (isShareSupported ? (
                <Button
                  className={
                    league.joinLinkId ? 'flex-1 rounded-r-none' : 'flex-1'
                  }
                  disabled={isSharing}
                  isLoading={isSharing}
                  onClick={() =>
                    share({
                      title: copy.joinMyFantasyOscarsLeague,
                      url: makeLink(league.joinLinkId || ''),
                    })
                  }
                  Icon={ShareIcon}
                >
                  {copy.invite}
                </Button>
              ) : (
                <Button
                  className={
                    league.joinLinkId ? 'flex-1 rounded-r-none' : 'flex-1'
                  }
                  onClick={() => {
                    copyToClipboard(makeLink(league.joinLinkId || ''));
                  }}
                  AcknowledgeClickIcon={CheckCircleIcon}
                  Icon={CopyIcon}
                >
                  {copy.invite}
                </Button>
              ))}
            {showShareButton && league.joinLinkId && (
              <Button
                className="flex-none rounded-l-none border-l-0"
                Icon={QrCodeIcon}
                onClick={() => setShowQrCodeModal(true)}
              />
            )}
            {showShareButton ? (
              <Action
                Icon={TrashIcon}
                onClick={() => {
                  if (
                    !window.confirm(copy.areYouSureYouWantToDeleteThisLeague)
                  )
                    return;

                  deleteLeagueMutation({ leagueId, userId: currentUser.id });
                  onClose();
                }}
              />
            ) : (
              <Action
                className="w-full text-neutral-500"
                onClick={() => {
                  if (
                    !window.confirm(copy.areYouSureYouWantToDeleteThisLeague)
                  )
                    return;

                  deleteLeagueMutation({ leagueId, userId: currentUser.id });
                  onClose();
                }}
              >
                {copy.DeleteLeague}
              </Action>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

let LeagueSettingsModal = ({
  isOpen,
  onClose,
  leagueId,
  showShareButton,
}: {
  isOpen: boolean;
  onClose: () => void;
  leagueId: string;
  showShareButton?: boolean;
}) => (
  <Modal isOpen={isOpen} onClose={onClose} disableBodyPadding className="flex">
    <Suspense fallback={<Loader />}>
      <LeagueSettingsModalContent
        onClose={onClose}
        leagueId={leagueId}
        showShareButton={showShareButton}
      />
    </Suspense>
  </Modal>
);

export default LeagueSettingsModal;
